import React from "react";
import { FaInstagram } from "react-icons/fa"
export default function AcademicAssignmentStructureGuide() {
  return (
    <section className="relative bg-[#02042b] text-white mt-12">
      {/* subtle glow accents */}
      <div className="pointer-events-none absolute inset-0 opacity-40"
           style={{
             background:
               "radial-gradient(900px 400px at 10% -10%, rgba(99,102,241,.18), transparent 60%), radial-gradient(800px 360px at 120% 0%, rgba(236,72,153,.15), transparent 55%)"
           }} />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* Hero */}
        <header className="text-center max-w-3xl mx-auto mb-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            How to Structure an Academic Assignment: A Step-by-Step Guide for University Students
          </h1>
          <p className="mt-3 text-gray-300">
            Get tips on how to structure academic assignments with expert tips, examples, and a step-by-step guide for students.
          </p>
        </header>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* TOC (sticky on desktop) */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white/5 backdrop-blur rounded-xl border border-white/10 p-5">
              <h2 className="text-lg font-semibold mb-3">Table of Contents</h2>
              <nav className="text-sm text-gray-300 space-y-2">
                <a href="#keypoints" className="block hover:text-white">Key Points</a>
                <a href="#prompt" className="block hover:text-white">Understanding the Assignment Prompt & Rubric</a>
                <a href="#planning" className="block hover:text-white">Assignment Planning</a>
                <a href="#intro" className="block hover:text-white">Crafting a Strong Introduction</a>
                <a href="#thesis" className="block hover:text-white">Building a Clear Thesis Statement</a>
                <a href="#body" className="block hover:text-white">Organizing Body Paragraphs</a>
                <a href="#imrad" className="block hover:text-white">Using the IMRaD Format</a>
                <a href="#conclusion" className="block hover:text-white">Writing a Cohesive Conclusion</a>
                <a href="#referencing" className="block hover:text-white">Academic Referencing & Formatting</a>
                <a href="#revising" className="block hover:text-white">Drafting & Revising</a>
                <a href="#faqs" className="block hover:text-white">FAQs</a>
                <a href="#help" className="block hover:text-white">Need Help?</a>
                <a href="#final" className="block hover:text-white">Final Thoughts</a>
                 <a href="#acknowledgement" className="block hover:text-white">Acknowledgement</a>
              </nav>
            </div>
          </aside>

          {/* Article */}
          <article className="lg:col-span-8 space-y-10">
            {/* Intro */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="intro" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">Introduction</h2>
              <p className="text-gray-200">
                It may be very daunting to write an academic assignment, but understanding how to structure an academic
                assignment is the first step toward making it manageable. A strong structure enables logic in your work,
                meets academic requirements, and helps you communicate ideas clearly.
              </p>
              <p className="text-gray-200 mt-3">
                Whether you are an undergraduate freshman or a postgraduate veteran, structure and cohesion are essential
                for success. A well-designed assignment reflects knowledge, critical thinking, and clarity of expression.
              </p>
            </section>

            {/* Key Points */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="keypoints" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-4">Key Points</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Break down the prompt and use the grading rubric effectively.</li>
                <li>Plan your assignment to save time and reduce stress.</li>
                <li>Write a strong introduction and clear thesis statement.</li>
                <li>Organize body paragraphs with topic sentences and logical flow.</li>
                <li>Master referencing, formatting, and final revisions for professional results.</li>
              </ul>
            </section>

            {/* Prompt & Rubric */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="prompt" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">1. Understanding the Assignment Prompt & Rubric</h2>
              <p className="text-gray-200">
                Before you write a word, read and analyze the brief. Highlight verbs like <em>evaluate</em>, <em>compare</em>, or
                <em> argue</em> and clarify what’s expected.
              </p>
              <p className="text-emerald-300/90 mt-3"><strong>Pro Tip:</strong> Keep the rubric beside you and make sure each section aligns with its criteria.</p>
            </section>

            {/* Planning */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="planning" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">2. Start with Effective Assignment Planning</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Understand scope & format:</strong> word count, structure, and deliverables.</li>
                <li><strong>Preliminary research:</strong> refine a specific, arguable thesis backed by credible sources.</li>
                <li><strong>Outline or mind map:</strong> arrange points and evidence to keep logic tight.</li>
                <li><strong>Schedule the process:</strong> drafting → revising → formatting → proofreading.</li>
              </ul>
            </section>

            {/* Strong Intro */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="intro-strong" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">3. Crafting a Strong Introduction</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li><strong>Context:</strong> give brief background and relevance (local or global).</li>
                <li><strong>Purpose:</strong> state what the paper evaluates, analyzes, argues, or proposes.</li>
                <li><strong>Thesis:</strong> finish the intro with a specific, arguable thesis statement.</li>
              </ul>
            </section>

            {/* Thesis */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="thesis" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">4. Building a Clear Thesis Statement</h2>
              <p className="text-gray-200">
                Treat your thesis like GPS: concrete, arguable, and sized to the word limit. Avoid vague claims—be specific and defensible.
              </p>
              <p className="text-gray-200 mt-3">
                <strong>Example:</strong> “There is a significant difference in academic writing performance between students who use
                structured outlines and those who don’t, as outlines enhance clarity and minimize writer’s block.”
              </p>
            </section>

            {/* Body Paragraphs */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="body" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">5. Organizing Body Paragraphs with Topic Sentences</h2>
              <ol className="list-decimal pl-6 space-y-2 text-gray-200">
                <li><strong>Topic sentence:</strong> clearly state the paragraph’s point.</li>
                <li><strong>Evidence:</strong> statistics, studies, cases, or examples.</li>
                <li><strong>Analysis:</strong> explain why the evidence matters and how it supports the thesis.</li>
                <li><strong>Transition:</strong> link to the next idea for smooth flow.</li>
                <li><strong>Logic:</strong> keep every sentence relevant to the argument.</li>
              </ol>
            </section>

            {/* IMRaD */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="imrad" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">6. Using the IMRaD Format (When Applicable)</h2>
              <p className="text-gray-200">For scientific/research tasks: <strong>I</strong>ntroduction, <strong>M</strong>ethods, <strong>R</strong>esults, <strong>D</strong>iscussion.</p>
            </section>

            {/* Conclusion */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="conclusion" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">7. Writing a Cohesive Conclusion</h2>
              <ol className="list-decimal pl-6 space-y-2 text-gray-200">
                <li><strong>Restate thesis</strong> in fresh words.</li>
                <li><strong>Summarize</strong> key arguments succinctly.</li>
                <li><strong>Final insight/recommendation</strong> that connects to real-world practice.</li>
                <li><strong>No new evidence</strong>—wrap up what you’ve presented.</li>
              </ol>
            </section>

            {/* Referencing */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="referencing" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">8. Academic Referencing & Formatting</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Use the specified style (APA/MLA/Harvard/Chicago) consistently.</li>
                <li>Match every in-text citation with the reference list entry.</li>
                <li>Include title page, ToC, appendices if required.</li>
                <li>Use clear headings (H1–H3) and page numbers.</li>
              </ul>
              <p className="text-gray-300 mt-3 text-sm">
                Example in-text: APA—(Smith, 2023); Harvard—(Smith 2023).
              </p>
            </section>

            {/* Drafting & Revising */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="revising" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">9. Drafting and Revising Effectively</h2>
              <h3 className="font-semibold text-white mb-2">First Draft Tips</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Focus on ideas, not perfection; follow your outline.</li>
                <li>Leave editing for later—aim for flow first.</li>
              </ul>
              <h3 className="font-semibold text-white mt-4 mb-2">Revision Techniques</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Take a short break for fresh perspective.</li>
                <li>Checklist: thesis connection, logic, transitions, style rules.</li>
                <li>Refine word choice; remove redundancy.</li>
              </ul>
              <h3 className="font-semibold text-white mt-4 mb-2">Peer Review</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-200">
                <li>Get feedback from a friend/tutor; read aloud to catch issues.</li>
              </ul>
            </section>

            {/* FAQs */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="faqs" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">10. Frequently Asked Questions</h2>
              <div className="space-y-3 text-gray-200">
                <p><strong>How many body paragraphs?</strong> Usually 3–5, depending on word count and complexity.</p>
                <p><strong>How do I align with the rubric?</strong> Use it as a checkpoint so each section meets evaluation criteria.</p>
                <p><strong>Revision vs Proofreading?</strong> Revision reshapes content/structure; proofreading fixes grammar and formatting.</p>
                <p><strong>Appendices?</strong> Include when extra data supports the argument but would clutter the main text.</p>
              </div>
            </section>

            {/* Help / CTA */}
            <section className="bg-gradient-to-r from-rose-500/20 to-indigo-500/20 rounded-xl border border-white/10 p-6" id="help" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-2">Need Help to Write Your Assignments?</h2>
              <p className="text-gray-200">
                Infinite Solution provides customized, plagiarism-free academic support worldwide—assignments, reports,
                dissertations, and theses—delivered on time with academic integrity.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href="/order" className="inline-flex items-center px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 font-semibold">
                  Get Help Now
                </a>
                <a href="/contactus" className="inline-flex items-center px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10">
                  Talk to a Specialist
                </a>
              </div>
            </section>

            {/* Final Thoughts */}
            <section className="bg-white/5 rounded-xl border border-white/10 p-6" id="final" style={{scrollMarginTop: '7rem'}}>
              <h2 className="text-2xl font-bold mb-3">Final Thoughts</h2>
              <p className="text-gray-200">
                A good structure guarantees clarity and professional polish. Plan ahead, write strong headings and thesis,
                keep logical flow, apply IMRaD where needed, and revise thoroughly.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-200 mt-3">
                <li>Plan properly and outline.</li>
                <li>Hook with a clear introduction and thesis.</li>
                <li>Use topic sentences and transitions.</li>
                <li>Format and reference consistently.</li>
              </ul>
              <p className="text-gray-200 mt-3">
                Ready to level up? Use this checklist and review your latest assignment today.
              </p>
            </section>

            <section
              id="acknowledgement"
              style={{ scrollMarginTop: "7rem" }}
              className="bg-gradient-to-r from-indigo-900/40 via-purple-900/30 to-pink-900/20 rounded-xl border border-white/10 p-6 mt-10 shadow-lg"
            >
              <h2 className="text-2xl sm:text-3xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-indigo-300 pb-2">
                Acknowledgement
              </h2>
            
              <p className="text-gray-200">
                The concept was first introduced by{" "}
                <span className="font-semibold text-white">Joy Barua</span>, who set the foundation for the content’s direction.
                <br />
                <a
                  href="https://www.instagram.com/j.b_12111990?igsh=ejBoa2dqbmI1OG4z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 text-pink-400 hover:text-pink-300 font-semibold"
                >
                  <FaInstagram /> @joybarua
                </a>
              </p>
            
              <p className="text-gray-200 mt-4">
                To shape the initial draft, we utilized advanced AI tools like <span className="font-semibold">ChatGPT</span> and{" "}
                <span className="font-semibold">Google Gemini</span>, ensuring speed, structure, and creativity. Once the draft was
                developed, our team at{" "}
                <a
                  href="https://www.infinitesolution.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 font-semibold hover:text-blue-300 underline"
                >
                  Infinite Solution
                </a>{" "}
                took over to polish it, optimizing for SEO, refining the tone for clarity, and aligning the message with the target
                audience.
              </p>
            
              <p className="text-gray-200 mt-4">
                This collaborative approach seamlessly blended human expertise with AI-driven efficiency, resulting in content that
                is impactful, high-performing, and highly relevant to readers.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
}
